import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { printerCapture, putToSignedUrl } from '../api';
import {
  photosStillNeeded, surveyPending, type CalibrationSession, type SurveyStatus,
} from '../model';
import { barMoved, CALIBRATION_SHOTS } from '../printers';

const POLL_MS = 2000;

/**
 * The phone page for a printer's calibration photographs (F11).
 *
 * The sheet capture page (F3) pointed at a printer: no sign-in, one printer,
 * authenticated by the printer-scoped token in the QR link. The same rules
 * apply for the same reasons -- the native camera through the file input, and
 * the token kept in the URL and component state, never in storage.
 *
 * It shows the survey's outcome as well as starting it, because the operator
 * is standing at the bed when it comes back: a failed survey means shooting
 * again, and a moved bar means finding out what knocked it, and both are
 * cheaper to act on there than after walking back to the workstation.
 *
 * As many photographs as `CALIBRATION_SHOTS` lists -- one, since the lens is
 * measured separately -- each in a session of its own, so an abandoned attempt
 * cannot leak into the next. The survey starts when the last one lands.
 */
export function CalibrationCapture() {
  const { printerId = '' } = useParams();
  const [params] = useSearchParams();
  const token = params.get('t') ?? '';
  const api = useRef(printerCapture(printerId, token)).current;

  const [printer, setPrinter] = useState<{ name: string } | null>(null);
  const [status, setStatus] = useState<SurveyStatus | null>(null);
  const [sent, setSent] = useState(false);
  const [session, setSession] = useState<CalibrationSession | null>(null);
  const [shot, setShot] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    try {
      const ctx = await api.context();
      setPrinter(ctx.printer);
      setStatus(ctx);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }, [api]);

  useEffect(() => { void load(); }, [load]);

  const pending = status ? surveyPending(status) : false;
  useEffect(() => {
    if (!pending) return;
    const timer = setInterval(() => { void load(); }, POLL_MS);
    return () => clearInterval(timer);
  }, [pending, load]);

  async function send(file: File) {
    setUploading(true);
    setError(null);
    try {
      const current = session ?? await api.calibrationUpload();
      if (!session) setSession(current);
      await putToSignedUrl(current.uploads[shot].url, file, 'image/jpeg');
      const next = shot + 1;
      setShot(next);
      if (next >= current.photos) setSent(true);
      await load();
    } catch (e) {
      // A link refused after this long has expired: the session is spent.
      setError(`${(e as Error).message}. If this keeps happening, start again.`);
    }
    finally { setUploading(false); }
  }

  function startAgain() {
    setSession(null);
    setShot(0);
    setSent(false);
    setError(null);
  }

  if (!token) {
    return (
      <div className="phone panel stop">
        <h2>Scan the QR code</h2>
        <p>This page needs the QR code shown on the Printers screen.</p>
      </div>
    );
  }

  if (error && !printer) {
    return (
      <div className="phone panel stop">
        <h2>Cannot open this printer</h2>
        <p>{error}</p>
        <p className="muted">Scan a fresh QR code from the Printers screen.</p>
      </div>
    );
  }

  // Only a result for the photograph sent from *this* page is shown as the
  // answer. Opening the link on a printer surveyed last month should not greet
  // the operator with last month's numbers as if they were today's.
  const survey = status?.survey;
  const answered = sent && !pending && !status?.surveyError && survey;
  const moved = survey?.movedSinceLast;
  const total = session?.photos ?? CALIBRATION_SHOTS.length;
  const shooting = !sent;
  // Only once this page has sent its last photograph does a pending survey
  // mean "measuring"; before that the survey is waiting for this phone.
  const measuring = sent && pending && (status ? photosStillNeeded(status) === 0 : true);

  return (
    <div className="phone stack">
      <header className="panel">
        <h2>{printer?.name ?? 'Loading…'}</h2>
        <p className="muted">Calibration photo{total > 1 ? 's' : ''}</p>
      </header>

      <section className="panel guidance">
        <h3>Before you shoot</h3>
        <ul>
          <li>Leave the calibration sheet exactly where it was printed.</li>
          {total > 1 && (
            <li>You will take {total} photos, each held as the page asks.</li>
          )}
          <li>Stand over the bed and hold the phone square to it.</li>
          <li>Both registration bars fully in frame, with every marker sticker.</li>
          <li>No glare across the sheet or over a bar.</li>
          <li>Tap to focus, wait for it to settle, then shoot.</li>
        </ul>
      </section>

      {sent && pending && !measuring && (
        <section className="panel">
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            Waiting for the photos to arrive…
          </p>
        </section>
      )}

      {measuring && (
        <section className="panel">
          <p className="busy-text">
            <span className="spinner" aria-hidden />
            Measuring the printer…
          </p>
        </section>
      )}

      {status?.surveyError && sent && (
        <section className="panel stop">
          <h3>The photo could not be measured</h3>
          <p>{status.surveyError.message}</p>
          <p className="muted">
            Usually the crosses or the marker stickers could not be found.
            Check the print came out, then shoot again.
          </p>
        </section>
      )}

      {answered && survey && (
        <section className="panel">
          <h3>Printer measured</h3>
          <p>
            {survey.markers} marker stickers and {survey.crossesFound} crosses
            found{survey.photos > 1 ? ` (in the worst of ${survey.photos} photos)` : ''}.
            The crosses fit to {survey.residualMil.toFixed(1)} thousandths of
            an inch.
          </p>
          {survey.markerSpreadMil !== null && (
            <p className="muted">
              The photos agree about every marker to within{' '}
              {survey.markerSpreadMil.toFixed(0)} thousandths of an inch.
            </p>
          )}
          {moved && (
            <p className={barMoved(moved) ? 'error' : 'muted'}>
              Since the last setup, the markers have moved by up to{' '}
              {moved.worstMil.toFixed(0)} thousandths of an inch
              (sticker {moved.worstMarker}).
            </p>
          )}
          {survey.notes.map((note) => <p key={note} className="error">{note}</p>)}
          <p className="muted">
            The workstation has it too. You can close this page.
          </p>
        </section>
      )}

      {shooting && (
        <section className="panel">
          {total > 1 && <h3>Photo {shot + 1} of {total}</h3>}
          {total > 1 && <p>{CALIBRATION_SHOTS[shot] ?? ''}</p>}
          <label className="capture-button">
            <input type="file" accept="image/*" capture="environment"
                   disabled={uploading}
                   onChange={(e) => {
                     const file = e.target.files?.[0];
                     // Cleared so the same input fires again for the next shot.
                     e.target.value = '';
                     if (file) void send(file);
                   }} />
            <span>
              {uploading
                ? 'Uploading…'
                : total > 1 ? `Shoot photo ${shot + 1}` : 'Shoot the calibration sheet'}
            </span>
          </label>
        </section>
      )}

      {(shot > 0 || sent) && (
        <section className="panel">
          <button disabled={uploading || measuring} onClick={startAgain}>
            {total > 1 ? 'Start again from photo 1' : 'Shoot it again'}
          </button>
        </section>
      )}

      {error && <p className="error">{error}</p>}
    </div>
  );
}
